import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SubmitShortcut } from "@/hooks/use-user-preferences";

const DISMISSED_STORAGE_KEY = "chat.enterToSendHint.dismissed";
export const OPEN_PREFERENCES_EVENT = "agno:open-preferences";

function wasDismissed() {
  try {
    return window.localStorage.getItem(DISMISSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function useEnterToSendHint({
  submitShortcut,
  isMobile,
  sessionId,
}: {
  submitShortcut: SubmitShortcut;
  isMobile: boolean;
  sessionId: string;
}) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(wasDismissed);
  const [autoHideKey, setAutoHideKey] = useState(0);
  const pendingNewlineCheckRef = useRef<number | undefined>(undefined);
  const plainEnterSinceSubmitRef = useRef(false);

  const clearPendingNewlineCheck = useCallback(() => {
    if (pendingNewlineCheckRef.current !== undefined) {
      window.clearTimeout(pendingNewlineCheckRef.current);
      pendingNewlineCheckRef.current = undefined;
    }
  }, []);

  const show = useCallback(() => {
    setOpen(true);
    setAutoHideKey((key) => key + 1);
  }, []);

  useEffect(() => {
    clearPendingNewlineCheck();
    plainEnterSinceSubmitRef.current = false;
    setOpen(false);
  }, [clearPendingNewlineCheck, sessionId]);

  useEffect(() => clearPendingNewlineCheck, [clearPendingNewlineCheck]);

  useEffect(() => {
    if (!open) return;
    const timeout = window.setTimeout(() => setOpen(false), 8_000);
    return () => window.clearTimeout(timeout);
  }, [autoHideKey, open]);

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key !== "Enter"
      || submitShortcut !== "ctrl_enter"
      || isMobile
      || dismissed
    ) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      clearPendingNewlineCheck();
      plainEnterSinceSubmitRef.current = false;
      return;
    }

    if (event.shiftKey) return;

    clearPendingNewlineCheck();
    plainEnterSinceSubmitRef.current = true;
    const textarea = event.currentTarget;
    const valueBeforeNewline = textarea.value;
    pendingNewlineCheckRef.current = window.setTimeout(() => {
      pendingNewlineCheckRef.current = undefined;
      if (textarea.value === valueBeforeNewline) show();
    }, 2_000);
  }, [clearPendingNewlineCheck, dismissed, isMobile, show, submitShortcut]);

  const notifySubmit = useCallback((text: string) => {
    if (
      submitShortcut === "ctrl_enter"
      && !isMobile
      && !dismissed
      && plainEnterSinceSubmitRef.current
      && !text.trimEnd().includes("\n")
    ) {
      show();
    }
    plainEnterSinceSubmitRef.current = false;
  }, [dismissed, isMobile, show, submitShortcut]);

  const hide = useCallback(() => setOpen(false), []);
  const dismiss = useCallback(() => {
    clearPendingNewlineCheck();
    plainEnterSinceSubmitRef.current = false;
    try {
      window.localStorage.setItem(DISMISSED_STORAGE_KEY, "1");
    } catch {
      // Storage can be unavailable in private or embedded contexts.
    }
    setDismissed(true);
    setOpen(false);
  }, [clearPendingNewlineCheck]);

  return { open, onKeyDown, hide, dismiss, notifySubmit };
}

export function EnterToSendHint({
  open,
  onOpenPreferences,
  onDismiss,
}: {
  open: boolean;
  onOpenPreferences: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <div
      aria-live="polite"
      className="flex items-center gap-2 rounded bg-muted/40 px-3 py-1.5 text-xs text-muted-foreground"
      data-testid="enter-to-send-hint"
      role="status"
    >
      <span>{t("chat:enterToSendHint.message")}</span>
      <Button
        className="h-auto p-0 text-xs"
        onClick={onOpenPreferences}
        size="sm"
        variant="link"
      >
        {t("chat:enterToSendHint.openPreferences")}
      </Button>
      <Button
        aria-label={t("chat:enterToSendHint.dismiss")}
        className="ml-auto size-5 shrink-0 p-0"
        onClick={onDismiss}
        size="icon"
        variant="ghost"
      >
        <X className="size-3" />
      </Button>
    </div>
  );
}
